import { PropsWithChildren } from "react";
import { Tabs as ArkTabs, ListCollection } from "@ark-ui/react";
import classnames from "classnames";

import Icon, { type IconNames } from "@/components/icon/icon";

import styles from "./tabs.module.css";

export type TabItem = {
  label: string;
  value: string;
  icon?: IconNames;
};

type TabsProps = ArkTabs.RootProps & {
  collection: ListCollection<TabItem>;
  /** Trigger visual style: pill "tabs" (default) or flat "rail" buttons. */
  variant?: "tabs" | "rail";
};

export const Tabs = ({
  collection,
  children,
  variant = "tabs",
  ...restProps
}: PropsWithChildren<TabsProps>) => {
  const isHorizontal = restProps.orientation === "horizontal";
  const isRail = variant === "rail";
  return (
    <ArkTabs.Root {...restProps}>
      <ArkTabs.List
        className={classnames(
          styles.list,
          isHorizontal && styles.listHorizontal,
          isRail && styles.listRail,
        )}
      >
        {collection.items.map((item) => (
          <ArkTabs.Trigger
            key={item.value}
            value={item.value}
            className={classnames(
              styles.trigger,
              isRail ? styles.triggerRail : styles.triggerTabs,
            )}
          >
            {item.icon && <Icon name={item.icon} size={16} />}
            {item.label}
          </ArkTabs.Trigger>
        ))}
        {/* The rail style shows selection via the button background, no bar. */}
        {!isRail && <ArkTabs.Indicator className={styles.indicator} />}
      </ArkTabs.List>

      {children}
    </ArkTabs.Root>
  );
};

export const TabsContent = ArkTabs.Content;
